import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { dab, maskedPatch, type Rect } from './pictureOps'

/**
 * The window and the two brushes (2026-10-07), shared by the Edit area card
 * (editNode.tsx) and Edit text's Fine-tune (textNode.tsx), so both handle
 * the same: a fixed-height window onto the full-size picture, and an Eraser
 * that takes the model's drawing away where the original should show, and a
 * Restore that paints it back, with a size and a flow.
 *
 * Moved here from editNode.tsx unchanged in behaviour, but for one thing: the
 * brush paints on any number of patches at once. The Edit area has one; Edit
 * text has one per part of the picture it sent.
 */

/** A patch over the original: the model's drawing, fitted, and how much of it shows. */
export interface BrushPatch {
  layer: HTMLCanvasElement
  mask: HTMLCanvasElement
  place: Rect
}

/** What a window shows: the scale (screen pixels per picture pixel) and the centre, in picture pixels. */
interface View {
  scale: number
  cx: number
  cy: number
}

/** Both windows are this tall; as wide as the card. */
const WINDOW_HEIGHT = 230

/**
 * The brush set by dragging (Huanrui, 2026-10-27), as in Photoshop: Alt and
 * the right button held, across the window sets the size, up and down the
 * flow -- up for more -- where the brush has one.
 */
export interface BrushSizing {
  size: number
  setSize: (n: number) => void
  min: number
  max: number
  flow?: number
  setFlow?: (n: number) => void
  flowMin?: number
  flowMax?: number
  /** Each change, to show the brush while it is set. */
  onChange?: () => void
  /** Where the drag began, in picture pixels, to draw the brush there; null when it ends. */
  at?: (at: { x: number; y: number } | null) => void
}

/** Which full-size picture each window copy was drawn from. */
const pictureSource = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>()

/* ------------------------------------------------------------------ */

/**
 * Drawn in layers since 2026-10-15 (Huanrui: the Edit area card was the
 * slowest thing on the board, and held about 800 MB of the graphics card).
 *
 *  - The picture, at the window's own size, made once from the full-size
 *    source and kept until the source, the view or the size changes. The
 *    full-size picture is no longer drawn again on every repaint.
 *  - `render` over it (the box, the patches): drawn again only when `stamp`
 *    changes. A window given no stamp draws it on every render, as before.
 *  - `overlay` on a second canvas on top (the brush ring and its sample),
 *    drawn on every render: moving the pointer redraws only this.
 *
 * And a canvas is resized only when its size really changes: setting a
 * canvas's width throws away its memory on the graphics card and asks for
 * new, which it used to do on every repaint.
 */
export function Window({
  source,
  frame,
  render,
  stamp,
  overlay,
  onLeft,
  cursor,
  onHover,
  sizing,
  children,
}: {
  source: HTMLCanvasElement | null
  /** What to show, in picture pixels: the whole picture, or the part round the box. */
  frame: Rect | null
  /** Draws on top of the picture, in picture pixels (the context is already transformed). */
  render?: (ctx: CanvasRenderingContext2D, scale: number) => void
  /** Anything that changes when what `render` draws changes. Left out, it is drawn on every render. */
  stamp?: string | number
  /** Draws on a layer of its own over the rest, in picture pixels, on every render. */
  overlay?: (ctx: CanvasRenderingContext2D, scale: number) => void
  /** A left-button press, in picture pixels; returns what to do as it moves and when it lets go. */
  onLeft?: (at: { x: number; y: number }) => { move: (at: { x: number; y: number }) => void; up: () => void } | null
  cursor?: string
  /** Where the pointer is, in picture pixels, or null once it has left. */
  onHover?: (at: { x: number; y: number } | null) => void
  /** Alt + right-drag sets the brush (BrushSizing). Left out, it does nothing here. */
  sizing?: BrushSizing
  children?: React.ReactNode
}) {
  const box = useRef<HTMLDivElement | null>(null)
  const canvas = useRef<HTMLCanvasElement | null>(null)
  const top = useRef<HTMLCanvasElement | null>(null)
  /** The picture at the window's size, and what it was made for. */
  const picture = useRef<{ canvas: HTMLCanvasElement; key: string } | null>(null)
  /** What the lower layer was last drawn for. */
  const drawnFor = useRef('')
  const [width, setWidth] = useState(0)
  const height = WINDOW_HEIGHT

  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => setWidth(el.offsetWidth)
    measure()
    const watch = new ResizeObserver(measure)
    watch.observe(el)
    return () => watch.disconnect()
  }, [])

  // The whole of `frame` in the window, centred.
  const view: View = useMemo(() => {
    const f = frame ?? (source ? { x: 0, y: 0, w: source.width, h: source.height } : { x: 0, y: 0, w: 1, h: 1 })
    const scale = width > 0 ? Math.min(width / f.w, height / f.h) : 1
    return { scale, cx: f.x + f.w / 2, cy: f.y + f.h / 2 }
  }, [frame, source, width, height])
  const viewRef = useRef(view)
  viewRef.current = view

  /** A point on the screen, in picture pixels. The card is inside the board's own zoom, so measured, not assumed. */
  const toPicture = useCallback(
    (clientX: number, clientY: number) => {
      const el = canvas.current
      if (!el) return { x: 0, y: 0 }
      const r = el.getBoundingClientRect()
      const sx = ((clientX - r.left) * width) / Math.max(1, r.width)
      const sy = ((clientY - r.top) * height) / Math.max(1, r.height)
      const v = viewRef.current
      return { x: (sx - width / 2) / v.scale + v.cx, y: (sy - height / 2) / v.scale + v.cy }
    },
    [width, height],
  )

  // Let go of the picture copy with the window.
  useEffect(
    () => () => {
      const held = picture.current
      if (held) {
        held.canvas.width = 0
        held.canvas.height = 0
      }
      picture.current = null
    },
    [],
  )

  // The lower layer: the picture and `render`, only when something they show changed.
  useEffect(() => {
    const el = canvas.current
    if (!el || width === 0 || !source || source.width === 0 || source.height === 0) return
    const dpr = window.devicePixelRatio || 1
    const w = Math.round(width * dpr)
    const h = Math.round(height * dpr)
    const sized = el.width !== w || el.height !== h
    if (sized) {
      el.width = w
      el.height = h
    }
    const { scale, cx, cy } = view
    const place = () => ctxPlace(dpr, scale, cx, cy, width, height)
    const viewKey = `${w}x${h}|${scale}|${cx}|${cy}`
    const holding = picture.current
    let fresh = false
    if (!holding || holding.key !== viewKey || pictureSource.get(holding.canvas) !== source) {
      // The picture as the window shows it, drawn from the full size once.
      const copy = holding?.canvas ?? document.createElement('canvas')
      if (copy.width !== w || copy.height !== h) {
        copy.width = w
        copy.height = h
      }
      const pctx = copy.getContext('2d')
      if (pctx) {
        pctx.setTransform(1, 0, 0, 1, 0, 0)
        pctx.clearRect(0, 0, w, h)
        pctx.imageSmoothingQuality = 'high'
        pctx.setTransform(...place())
        pctx.drawImage(source, 0, 0)
      }
      pictureSource.set(copy, source)
      picture.current = { canvas: copy, key: viewKey }
      fresh = true
    }
    const key = stamp === undefined ? '' : `${viewKey}|${String(stamp)}`
    if (!sized && !fresh && key && key === drawnFor.current) return
    drawnFor.current = key
    const ctx = el.getContext('2d')
    if (!ctx) return
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, w, h)
    ctx.drawImage(picture.current!.canvas, 0, 0)
    ctx.imageSmoothingQuality = 'high'
    ctx.setTransform(...place())
    render?.(ctx, scale)
  })

  // The upper layer: cleared and drawn on every render, which costs next to nothing.
  useEffect(() => {
    const el = top.current
    if (!el || width === 0) return
    const dpr = window.devicePixelRatio || 1
    const w = Math.round(width * dpr)
    const h = Math.round(height * dpr)
    if (el.width !== w || el.height !== h) {
      el.width = w
      el.height = h
    }
    const ctx = el.getContext('2d')
    if (!ctx) return
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, w, h)
    if (!overlay || !source) return
    const { scale, cx, cy } = view
    ctx.setTransform(...ctxPlace(dpr, scale, cx, cy, width, height))
    overlay(ctx, scale)
  })

  const drag = useRef<{ move: (at: { x: number; y: number }) => void; up: () => void } | null>(null)
  /** Alt + right-drag under way: where it began on the screen, and the brush then. */
  const setting = useRef<{ x: number; y: number; size: number; flow: number; at: { x: number; y: number } } | null>(null)
  const sizingRef = useRef(sizing)
  sizingRef.current = sizing
  /** An Alt let go after setting the brush is not Windows' "Alt opens the menu". */
  const usedAlt = useRef(false)
  useEffect(() => {
    const up = (event: KeyboardEvent) => {
      if (event.key === 'Alt' && usedAlt.current) {
        event.preventDefault()
        usedAlt.current = false
      }
    }
    window.addEventListener('keyup', up, true)
    return () => window.removeEventListener('keyup', up, true)
  }, [])

  return (
    <div
      ref={box}
      className="enode__window nodrag nopan"
      style={{ height, cursor: cursor ?? 'default' }}
      onPointerLeave={() => onHover?.(null)}
      onContextMenu={(event) => {
        // Alt + right-drag is the brush, not the board's menu.
        if (sizing && (event.altKey || setting.current)) {
          event.preventDefault()
          event.stopPropagation()
        }
      }}
      onPointerDown={(event) => {
        const s = sizingRef.current
        if (event.button === 2 && event.altKey && s && source) {
          event.preventDefault()
          event.stopPropagation()
          event.currentTarget.setPointerCapture(event.pointerId)
          const at = toPicture(event.clientX, event.clientY)
          setting.current = { x: event.clientX, y: event.clientY, size: s.size, flow: s.flow ?? 0, at }
          usedAlt.current = true
          s.at?.(at)
          return
        }
        if (event.button !== 0 || !source || !onLeft) return
        event.stopPropagation()
        const held = onLeft(toPicture(event.clientX, event.clientY))
        if (!held) return
        event.currentTarget.setPointerCapture(event.pointerId)
        drag.current = held
      }}
      onPointerMove={(event) => {
        const held = setting.current
        const s = sizingRef.current
        if (held && s) {
          // Only the brush: nothing under the window moves with it.
          event.stopPropagation()
          // Screen pixels: the brush is measured on the screen too.
          const size = Math.round(Math.min(s.max, Math.max(s.min, held.size + (event.clientX - held.x))))
          if (size !== s.size) s.setSize(size)
          if (s.setFlow && s.flow !== undefined) {
            const flow = Math.round(
              Math.min(s.flowMax ?? 100, Math.max(s.flowMin ?? 1, held.flow - (event.clientY - held.y) / 2)),
            )
            if (flow !== s.flow) s.setFlow(flow)
          }
          s.onChange?.()
          s.at?.(held.at)
          return
        }
        const at = toPicture(event.clientX, event.clientY)
        onHover?.(at)
        drag.current?.move(at)
      }}
      onPointerUp={(event) => {
        if (setting.current) {
          setting.current = null
          if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
          sizingRef.current?.at?.(null)
          return
        }
        const d = drag.current
        drag.current = null
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
        d?.up()
      }}
    >
      <canvas ref={canvas} className="enode__canvas" />
      <canvas ref={top} className="enode__canvas" />
      {children}
    </div>
  )
}


/* ------------------------------------------------------------------ */

/**
 * The brushes' state, and what the window needs to paint with them and to
 * show what they did. `version` changes whenever the patches are rebuilt.
 */
export function useBrush(patches: BrushPatch[], version: number | undefined) {
  const [tool, setTool] = useState<'erase' | 'restore'>('erase')
  const [brush, setBrush] = useState(40)
  const [flow, setFlow] = useState(35)
  const [painted, setPainted] = useState(0)
  const masked = useRef<(HTMLCanvasElement | undefined)[]>([])
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null)
  const frame = useRef(0)
  /** Shown in the middle of the window while a slider moves, and a moment after. */
  const [sample, setSample] = useState(false)
  /** The dashed line round the part the model drew: shown or not. */
  const [outline, setOutline] = useState(true)
  const sampleTimer = useRef(0)
  const showSample = () => {
    setSample(true)
    window.clearTimeout(sampleTimer.current)
    sampleTimer.current = window.setTimeout(() => setSample(false), 1200)
  }
  useEffect(() => () => window.clearTimeout(sampleTimer.current), [])
  useEffect(() => () => cancelAnimationFrame(frame.current), [])

  // Each patch as its mask lets it through, redrawn after a stroke or a new result.
  const refreshMasked = useCallback(() => {
    masked.current = patches.map((patch, index) => maskedPatch(patch.layer, patch.mask, masked.current[index]))
  }, [patches])
  useEffect(() => {
    refreshMasked()
    setPainted((n) => n + 1)
  }, [refreshMasked, version])
  const redraw = () => {
    if (frame.current) return
    frame.current = requestAnimationFrame(() => {
      frame.current = 0
      refreshMasked()
      setPainted((n) => n + 1)
    })
  }

  const scaleRef = useRef(1)
  /** A press in the window: a stroke, on every patch the brush touches. */
  const startStroke = (at: { x: number; y: number }) => {
    if (patches.length === 0) return null
    const radius = () => brush / 2 / scaleRef.current
    const erase = tool === 'erase'
    const stroke = (p: { x: number; y: number }) => {
      const r = radius()
      for (const patch of patches) {
        const { place } = patch
        if (p.x < place.x - r || p.y < place.y - r || p.x > place.x + place.w + r || p.y > place.y + place.h + r) continue
        dab(patch.mask, place, p.x, p.y, r, flow / 100, erase)
      }
    }
    stroke(at)
    let last = at
    redraw()
    return {
      move: (to: { x: number; y: number }) => {
        // Dabs along the way, a third of the brush apart, so a fast stroke is still a line.
        const step = Math.max(1, radius() / 3)
        const dist = Math.hypot(to.x - last.x, to.y - last.y)
        for (let t = step; t <= dist; t += step) {
          stroke({ x: last.x + ((to.x - last.x) * t) / dist, y: last.y + ((to.y - last.y) * t) / dist })
        }
        if (dist >= step) last = to
        redraw()
      },
      up: () => redraw(),
    }
  }

  /** Over the picture in the window: the patches and the dashed outlines. Drawn again only when `stamp` changes. */
  const render = (ctx: CanvasRenderingContext2D, scale: number, outlines: Rect[]) => {
    scaleRef.current = scale
    patches.forEach((patch, index) => {
      const shown = masked.current[index]
      if (shown && shown.width > 0 && shown.height > 0) ctx.drawImage(shown, patch.place.x, patch.place.y, patch.place.w, patch.place.h)
    })
    if (outline) {
      ctx.save()
      ctx.setLineDash([4 / scale, 4 / scale])
      ctx.lineWidth = 1.5 / scale
      ctx.strokeStyle = 'rgba(255,255,255,0.75)'
      for (const r of outlines) ctx.strokeRect(r.x, r.y, r.w, r.h)
      ctx.setLineDash([])
      ctx.restore()
    }
  }
  /** What `render` shows changed: a stroke, a new result, the outline switched. */
  const stamp = `${painted}|${outline ? 1 : 0}|${version ?? 0}`

  /** On a layer of its own: the brush ring and its sample. Moving the pointer redraws only this. */
  const overlay = (ctx: CanvasRenderingContext2D, scale: number) => {
    scaleRef.current = scale
    ctx.save()
    if (hover) {
      ctx.lineWidth = 1.5 / scale
      ctx.strokeStyle = 'rgba(255,255,255,0.9)'
      ctx.beginPath()
      ctx.arc(hover.x, hover.y, brush / 2 / scale, 0, Math.PI * 2)
      ctx.stroke()
    }
    ctx.restore()
    // The brush as it now is, at the pointer, while it is being set: its
    // size, and its flow as how strongly it marks (2026-10-27: no longer in
    // the middle of the window -- Huanrui looks at the pointer).
    if (sample && hover) {
      const dpr = window.devicePixelRatio || 1
      const m = ctx.getTransform()
      const cx = m.a * hover.x + m.e
      const cy = m.d * hover.y + m.f
      const r = (brush / 2) * dpr
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r)
      g.addColorStop(0, `rgba(255,255,255,${flow / 100})`)
      g.addColorStop(0.5, `rgba(255,255,255,${(flow / 100) * 0.6})`)
      g.addColorStop(1, 'rgba(255,255,255,0)')
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.arc(cx, cy, r, 0, Math.PI * 2)
      ctx.fill()
      ctx.lineWidth = 1.5 * dpr
      ctx.strokeStyle = 'rgba(255,255,255,0.95)'
      ctx.stroke()
      ctx.font = `${11 * dpr}px Aptos, system-ui, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      ctx.fillStyle = 'rgba(0,0,0,0.6)'
      const label = `${brush} px · ${flow}%`
      const w = ctx.measureText(label).width + 12 * dpr
      const y = Math.min(ctx.canvas.height - 22 * dpr, cy + r + 8 * dpr)
      ctx.fillRect(cx - w / 2, y - 3 * dpr, w, 18 * dpr)
      ctx.fillStyle = '#ffffff'
      ctx.fillText(label, cx, y)
      ctx.restore()
    }
  }

  /** Alt + right-drag in the window: size across, flow up and down (Window, BrushSizing). */
  const sizing: BrushSizing = {
    size: brush,
    setSize: setBrush,
    min: 4,
    max: 200,
    flow,
    setFlow,
    flowMin: 5,
    flowMax: 100,
    onChange: showSample,
    at: (at) => at && setHover(at),
  }

  return { tool, setTool, brush, setBrush, flow, setFlow, outline, setOutline, showSample, setHover, startStroke, render, overlay, stamp, sizing }
}

/** Eraser, Restore, Outline, and the Size and Flow sliders; `children` is the button under them. */
export function BrushControls({
  brush: b,
  children,
}: {
  brush: ReturnType<typeof useBrush>
  children?: React.ReactNode
}) {
  return (
    <div className="enode__brush nodrag nopan">
      <div className="pnode__chips">
        <button
          type="button"
          className={`pnode__chip${b.tool === 'erase' ? ' pnode__chip--on' : ''}`}
          onClick={() => b.setTool('erase')}
          title="Take the new drawing away, so the original shows"
        >
          Eraser
        </button>
        <button
          type="button"
          className={`pnode__chip${b.tool === 'restore' ? ' pnode__chip--on' : ''}`}
          onClick={() => b.setTool('restore')}
          title="Paint the new drawing back where it was erased"
        >
          Restore
        </button>
        <button
          type="button"
          className={`pnode__chip${b.outline ? ' pnode__chip--on' : ''}`}
          onClick={() => b.setOutline((on) => !on)}
          title="Show or hide the dashed line round the part the model drew"
        >
          Outline
        </button>
      </div>
      <label className="enode__row">
        <span>Size</span>
        <input
          className="nodrag"
          type="range"
          min={4}
          max={200}
          value={b.brush}
          onPointerDown={(event) => event.stopPropagation()}
          onChange={(event) => {
            b.setBrush(Number(event.target.value))
            b.showSample()
          }}
        />
        <em>{b.brush} px</em>
      </label>
      <label className="enode__row">
        <span>Flow</span>
        <input
          className="nodrag"
          type="range"
          min={5}
          max={100}
          value={b.flow}
          onPointerDown={(event) => event.stopPropagation()}
          onChange={(event) => {
            b.setFlow(Number(event.target.value))
            b.showSample()
          }}
        />
        <em>{b.flow}%</em>
      </label>
      <p className="xnode__hint">Or hold Alt and drag with the right button: left and right for the size, up and down for the flow.</p>
      {children}
    </div>
  )
}

/** The window's transform: picture pixels to its own canvas pixels. */
function ctxPlace(dpr: number, scale: number, cx: number, cy: number, width: number, height: number): [number, number, number, number, number, number] {
  return [dpr * scale, 0, 0, dpr * scale, dpr * (width / 2 - cx * scale), dpr * (height / 2 - cy * scale)]
}
